import {useState} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {DataTable, type Column} from '@common-web/components/data-table';
import {FileText, Download, Calendar, User, AlertCircle} from 'lucide-react';
import {RfcAttachmentType} from '@common/types/rfc';
import {trpc} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';

type Attachment = {
  id: string;
  fileName: string;
  fileType: RfcAttachmentType;
  fileSize: number;
  contentType?: string | null;
  createdAt: string | Date;
  uploadedByUser?: {id: string; name: string; email: string} | null;
};

const FILE_TYPE_LABEL: Record<RfcAttachmentType, string> = {
  [RfcAttachmentType.CENSUS]: 'Employee Census',
  [RfcAttachmentType.LOCATION_DETAILS]: 'Location Details',
  [RfcAttachmentType.BANK_LETTER]: 'Bank Letter',
  [RfcAttachmentType.VOIDED_CHECK]: 'Voided Check',
  [RfcAttachmentType.SIGNED_ACH]: 'Signed ACH Form',
  [RfcAttachmentType.OTHER]: 'Other Document',
};

const FILE_TYPE_COLOR: Record<RfcAttachmentType, string> = {
  [RfcAttachmentType.CENSUS]: 'bg-blue-100 text-blue-800',
  [RfcAttachmentType.LOCATION_DETAILS]: 'bg-green-100 text-green-800',
  [RfcAttachmentType.BANK_LETTER]: 'bg-purple-100 text-purple-800',
  [RfcAttachmentType.VOIDED_CHECK]: 'bg-orange-100 text-orange-800',
  [RfcAttachmentType.SIGNED_ACH]: 'bg-red-100 text-red-800',
  [RfcAttachmentType.OTHER]: 'bg-gray-100 text-gray-800',
};

function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function GroupRfcFileList({attachments}: {attachments: Attachment[]}) {
  const [downloading, setDownloading] = useState<Set<string>>(new Set());

  const downloadMutation = trpc.group.rfc.generateAttachmentDownloadUrl.useMutation();

  const handleDownload = async (attachment: Attachment) => {
    if (downloading.has(attachment.id)) return;
    setDownloading((prev) => new Set(prev).add(attachment.id));
    try {
      const {downloadUrl} = await downloadMutation.mutateAsync({attachmentId: attachment.id});
      window.open(downloadUrl, '_blank');
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setDownloading((prev) => {
        const next = new Set(prev);
        next.delete(attachment.id);
        return next;
      });
    }
  };

  const columns: Column<Attachment>[] = [
    {
      id: 'file',
      header: 'File',
      render: (attachment) => (
        <div className='flex items-center gap-3'>
          <FileText className='h-4 w-4 text-blue-500' />
          <div>
            <div className='font-medium text-sm'>{attachment.fileName}</div>
            {attachment.contentType && (
              <div className='text-xs text-muted-foreground'>{attachment.contentType}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      render: (attachment) => (
        <Badge variant='outline' className={`text-xs ${FILE_TYPE_COLOR[attachment.fileType]}`}>
          {FILE_TYPE_LABEL[attachment.fileType]}
        </Badge>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      render: (attachment) => (
        <span className='text-sm'>{formatFileSize(attachment.fileSize)}</span>
      ),
    },
    {
      id: 'uploaded',
      header: 'Uploaded',
      render: (attachment) => (
        <div className='space-y-1'>
          <div className='flex items-center gap-2 text-sm'>
            <Calendar className='h-3 w-3' />
            {new Date(attachment.createdAt).toLocaleDateString()}
          </div>
          {attachment.uploadedByUser && (
            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
              <User className='h-3 w-3' />
              {attachment.uploadedByUser.name}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (attachment) => (
        <Button
          variant='outline'
          size='sm'
          onClick={() => handleDownload(attachment)}
          disabled={downloading.has(attachment.id)}
        >
          {downloading.has(attachment.id) ? (
            <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600' />
          ) : (
            <Download className='h-4 w-4' />
          )}
        </Button>
      ),
    },
  ];

  if (attachments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <FileText className='h-5 w-5' />
            Attachments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-8'>
            <FileText className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
            <p className='text-muted-foreground'>No files attached</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <FileText className='h-5 w-5' />
          Attachments ({attachments.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-4'>
          <DataTable
            columns={columns}
            data={attachments}
            rowKey={(attachment) => attachment.id}
            emptyMessage='No files attached'
          />
          {downloadMutation.error && (
            <Alert>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>
                Failed to download file: {downloadMutation.error.message}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
